import React, { useState, useEffect } from "react";
import cookie from "react-cookies";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Fab,
  Zoom,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FlagIcon from "@mui/icons-material/Flag";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  getVendorAssessmentQuestionnaire,
  getVendorAssessmentSubcategories,
  updateVendorAssessmentAnswer,
  saveVendorAssessmentAttachment,
  deleteVendorAssessmentAttachment,
  submitVendorAssessment,
  sendForVendorReview,
} from "./vendorApiService";
import SuccessModal from "./SuccessModal";
import ReusableButton from "../../UpdatedComponents/ReusableButton";
import { useSnackbar } from "../../ReusableComponents/SnackbarManager.js"
import "../compliance/PolicyDoc.css";
import { buildTenantApiUrl } from "../../auth/utilities.js";

const PRIMARY = "#001b2b";
const ACCENT = "#00bcd4";
const SECTION_HEADER_BG = "#e0f7fa";

const VENDOR_CHOICE_OPTIONS = {
  YES: "YES",
  NO: "NO",
  PARTIAL: "PARTIAL",
  NOT_APPLICABLE: "NOT_APPLICABLE",
};

const CHOICE_LABELS = {
  [VENDOR_CHOICE_OPTIONS.YES]: "Yes",
  [VENDOR_CHOICE_OPTIONS.NO]: "No",
  [VENDOR_CHOICE_OPTIONS.PARTIAL]: "Partial",
  [VENDOR_CHOICE_OPTIONS.NOT_APPLICABLE]: "Not Applicable",
};

const INSTRUCTIONS = [
  {
    step: 1,
    title: "Access the Vendor Section",
    text: "Navigate to the vendor section to access your assigned questionnaire.",
  },
  {
    step: 2,
    title: "Open the Questionnaire",
    text: "Click on the questionnaire link to begin the assessment process.",
  },
  {
    step: 3,
    title: "Understand the Questionnaire Layout",
    text: "Review the questionnaire structure with sections and categories.",
  },
  {
    step: 4,
    title: "Begin Filling the Questionnaire",
    text: "Answer each question by selecting the appropriate response:",
    bullets: [
      "Yes - The control is fully implemented",
      "Partial - The control is partially implemented",
      "No - The control is not implemented",
      "Not Applicable - The control does not apply to your organization",
    ],
  },
  {
    step: 5,
    title: "Save Progress (Optional)",
    text: "Your answers are automatically saved. Click 'Save as Draft' to manually save your progress.",
  },
  {
    step: 6,
    title: "Review and Submit",
    text: "Once all questions are answered, review your responses and click 'Submit Questionnaire'.",
  },
];


const VendorAssessmentQuestionnaire = ({ token, assessmentId, vendorName, deliveryMode, onSubmitSuccess }) => {
  const isSelfMode = deliveryMode?.toLowerCase() === "self";
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sendingForReview, setSendingForReview] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [attachments, setAttachments] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [savingAnswers, setSavingAnswers] = useState({});
  const [shakingComments, setShakingComments] = useState({});
  const [reviewEmailDialogOpen, setReviewEmailDialogOpen] = useState(false);
  const [reviewToEmail, setReviewToEmail] = useState("");
  const [reviewCcEmails, setReviewCcEmails] = useState("");
  const [assessmentStatus, setAssessmentStatus] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const showSnackbar = useSnackbar();

  // Scroll-to-top visibility listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Check if any No/Partial answer is missing a comment (used to disable submit)
  const hasIncompleteComments = () => {
    if (!questionnaire?.categories) return false;
    for (const category of questionnaire.categories) {
      for (const subcategory of category.subcategories || []) {
        for (const section of subcategory.sections || []) {
          for (const question of section.questions || []) {
            const answerId = question.vendor_risk_assessment_answer_id;
            const answer = answers[answerId];
            if (
              (answer === VENDOR_CHOICE_OPTIONS.NO || answer === VENDOR_CHOICE_OPTIONS.PARTIAL) &&
              (!comments[answerId] || comments[answerId].trim() === "")
            ) {
              return true;
            }
          }
        }
      }
    }
    return false;
  };

  // Check if all questions are answered and required evidence is attached
  const isSubmissionReady = () => {
    if (!questionnaire?.categories) return false;
    for (const category of questionnaire.categories) {
      for (const subcategory of category.subcategories || []) {
        for (const section of subcategory.sections || []) {
          for (const question of section.questions || []) {
            const answerId = question.vendor_risk_assessment_answer_id;
            const answer = answers[answerId];
            if (!answer) return false;
            if (
              answer === VENDOR_CHOICE_OPTIONS.YES &&
              question.is_artifact_mandatory &&
              (!attachments[answerId] || attachments[answerId].length === 0)
            ) {
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  useEffect(() => {
    loadQuestionnaire();
  }, [token, assessmentId]);

  const loadQuestionnaire = async () => {
    setLoading(true);
    try {
      const params = {};
      if (token) {
        params.token = token;
      } else if (assessmentId) {
        params.assessment_id = assessmentId;
      }

      // Step 1: Fetch subcategories for this assessment
      const subcatResponse = await getVendorAssessmentSubcategories(params);

      // Handle both old format (array) and new format (object with subcategories + emails)
      const subcategories = Array.isArray(subcatResponse) ? subcatResponse : subcatResponse?.subcategories || [];
      if (subcatResponse && !Array.isArray(subcatResponse)) {
        // Pre-fill email fields from assessment data
        if (subcatResponse.to_email) setReviewToEmail(subcatResponse.to_email);
        if (subcatResponse.cc_emails) setReviewCcEmails(subcatResponse.cc_emails);
        if (subcatResponse.assessment_status) setAssessmentStatus(subcatResponse.assessment_status);
      }

      if (!subcategories || subcategories.length === 0) {
        showSnackbar("No subcategories found for this assessment", "error");
        setLoading(false);
        return;
      }

      // Step 2: For each subcategory, fetch its sections/questions
      const subcatDataPromises = subcategories.map(async (subcat) => {
        const qParams = {
          ...params,
          subcategory_id: subcat.id,
          subcategory_type: subcat.type,
        };
        const sections = await getVendorAssessmentQuestionnaire(qParams);
        return {
          subcategory: subcat,
          sections: Array.isArray(sections) ? sections : [],
        };
      });

      const subcatResults = await Promise.all(subcatDataPromises);

      // Step 3: Build the categories/subcategories/sections structure the component expects
      const builtData = {
        categories: [
          {
            category_name: "Vendor Risk Assessment",
            subcategories: subcatResults.map((result) => ({
              subcategory_name: result.subcategory.name,
              subcategory_id: result.subcategory.id,
              subcategory_type: result.subcategory.type,
              sections: result.sections.map((section) => ({
                section_id: section.section_id,
                section_name: section.section_name,
                section_type: section.section_type,
                weightage: section.weightage,
                questions: section.questions || [],
              })),
            })),
          },
        ],
      };

      setQuestionnaire(builtData);

      // Initialize answers and comments from existing data
      const initialAnswers = {};
      const initialComments = {};
      const initialAttachments = {};

      builtData.categories.forEach((category) => {
        category.subcategories?.forEach((subcategory) => {
          subcategory.sections?.forEach((section) => {
            section.questions?.forEach((question) => {
              const answerId = question.vendor_risk_assessment_answer_id;
              if (question.vendor_questionnaire_choice) {
                initialAnswers[answerId] = question.vendor_questionnaire_choice;
              }
              if (question.comment) {
                initialComments[answerId] = question.comment;
              }
              if (question.attachments && question.attachments.length > 0) {
                initialAttachments[answerId] = question.attachments;
              }
            });
          });
        });
      });

      setAnswers(initialAnswers);
      setComments(initialComments);
      setAttachments(initialAttachments);
    } catch (error) {
      showSnackbar(error.message || "Failed to load questionnaire", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleAnswerChange = async (answerId, value, questionData) => {
    // Update local state immediately
    setAnswers((prev) => ({ ...prev, [answerId]: value }));

    // Trigger shake on the comment box when No or Partial is selected
    if (value === VENDOR_CHOICE_OPTIONS.NO || value === VENDOR_CHOICE_OPTIONS.PARTIAL) {
      setShakingComments((prev) => ({ ...prev, [answerId]: true }));
      setTimeout(() => {
        setShakingComments((prev) => ({ ...prev, [answerId]: false }));
      }, 820);
    }

    // Auto-save the answer
    setSavingAnswers((prev) => ({ ...prev, [answerId]: true }));
    try {
      const payload = {
        vendor_risk_assessment_answer_id: answerId,
        vendor_questionnaire_choice: value,
        comment: comments[answerId] || "",
      };

      if (token) {
        payload.token = token;
      } else if (assessmentId) {
        payload.assessment_id = assessmentId;
      }

      await updateVendorAssessmentAnswer(payload);
    } catch (error) {
      showSnackbar("Failed to save answer", "error");
    } finally {
      setSavingAnswers((prev) => ({ ...prev, [answerId]: false }));
    }
  };

  const handleCommentChange = (answerId, value) => {
    setComments((prev) => ({ ...prev, [answerId]: value }));
  };

  const handleCommentBlur = async (answerId) => {
    // Save comment when user leaves the field
    try {
      const payload = {
        vendor_risk_assessment_answer_id: answerId,
        vendor_questionnaire_choice: answers[answerId] || "",
        comment: comments[answerId] || "",
      };

      if (token) {
        payload.token = token;
      } else if (assessmentId) {
        payload.assessment_id = assessmentId;
      }

      await updateVendorAssessmentAnswer(payload);
    } catch (error) {
      showSnackbar("Failed to save comment", "error");
    }
  };

  const handleFileAttach = async (answerId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar("Invalid file type. Only PDF, JPG, and PNG are allowed.", "error");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("File size must be less than 5MB", "error");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("vendor_risk_assessment_answer_id", answerId);
      formData.append("file", file);

      // Build URL with token or assessment_id
      let url = buildTenantApiUrl("tprm/save-vendor-assessment-attachment/", "en");
      if (token) {
        url += `?token=${encodeURIComponent(token)}`;
      } else if (assessmentId) {
        url += `?assessment_id=${encodeURIComponent(assessmentId)}`;
      }

      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": cookie.load("csrftoken") },
        body: formData,
      });

      const response = await res.json();

      if (!res.ok) {
        throw new Error(response.error || "Failed to upload evidence");
      }
      
      // Update attachments state
      setAttachments((prev) => ({
        ...prev,
        [answerId]: response.attachments || [{ file_name: file.name, attachment_id: response.attachment_id }],
      }));
      
      showSnackbar("Evidence uploaded successfully", "success");
    } catch (error) {
      showSnackbar(error.message || "Failed to upload evidence", "error");
    }
  };

  const handleFileRemove = async (answerId, attachmentId) => {
    try {
      const payload = { attachment_id: attachmentId };
      if (token) {
        payload.token = token;
      } else if (assessmentId) {
        payload.assessment_id = assessmentId;
      }

      await deleteVendorAssessmentAttachment(payload);
      
      setAttachments((prev) => {
        const newAttachments = { ...prev };
        if (newAttachments[answerId]) {
          newAttachments[answerId] = newAttachments[answerId].filter(
            (att) => att.attachment_id !== attachmentId
          );
          if (newAttachments[answerId].length === 0) {
            delete newAttachments[answerId];
          }
        }
        return newAttachments;
      });
      
      showSnackbar("Evidence removed successfully", "success");
    } catch (error) {
      showSnackbar("Failed to remove evidence", "error");
    }
  };

  const handleSaveDraft = () => {
    // Answers are auto-saved on every change, so this just provides user feedback
    showSnackbar("Your progress has been automatically saved", "info");
  };

  const handleSendForReview = async () => {
    const validation = validateSubmission();
    if (!validation.isValid) {
      showSnackbar(validation.message, "error");
      return;
    }
    // Open email dialog to collect vendor email before sending
    setReviewEmailDialogOpen(true);
  };

  const handleConfirmSendForReview = async () => {
    if (!reviewToEmail.trim()) {
      showSnackbar("Vendor email is required", "error");
      return;
    }

    setReviewEmailDialogOpen(false);
    setSendingForReview(true);
    try {
      const ccList = reviewCcEmails
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e);
      const payload = {
        assessment_id: assessmentId,
        to_email: reviewToEmail.trim(),
        cc_emails: ccList,
      };
      await sendForVendorReview(payload);
      setShowSuccess(true);
      if (onSubmitSuccess) {
        setTimeout(() => {
          onSubmitSuccess();
        }, 2000);
      }
    } catch (error) {
      showSnackbar(error.message || "Failed to send for vendor review", "error");
    } finally {
      setSendingForReview(false);
    }
  };

  const validateSubmission = () => {
    if (!questionnaire?.categories) return { isValid: false, message: "No questionnaire data" };

    const allQuestions = [];
    questionnaire.categories.forEach((category) => {
      category.subcategories?.forEach((subcategory) => {
        subcategory.sections?.forEach((section) => {
          section.questions?.forEach((question) => {
            allQuestions.push(question);
          });
        });
      });
    });

    // Check if all questions are answered
    for (const question of allQuestions) {
      const answerId = question.vendor_risk_assessment_answer_id;
      const answer = answers[answerId];

      if (!answer) {
        return {
          isValid: false,
          message: "Please answer all questions before submitting",
        };
      }

      // Check if evidence is required
      if (
        answer === VENDOR_CHOICE_OPTIONS.YES &&
        question.is_artifact_mandatory &&
        (!attachments[answerId] || attachments[answerId].length === 0)
      ) {
        return {
          isValid: false,
          message: "Please upload required supporting evidence for all 'Yes' answers",
        };
      }

      // Check if comment is required for NO or PARTIAL
      if (
        (answer === VENDOR_CHOICE_OPTIONS.NO || answer === VENDOR_CHOICE_OPTIONS.PARTIAL) &&
        (!comments[answerId] || comments[answerId].trim() === "")
      ) {
        return {
          isValid: false,
          message: "Please provide comments for all 'No' and 'Partial' answers",
        };
      }
    }

    return { isValid: true };
  };

  const handleSubmit = async () => {
    const validation = validateSubmission();
    if (!validation.isValid) {
      showSnackbar(validation.message, "error");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        vendor_risk_assessment_id: questionnaire.vendor_risk_assessment_id || assessmentId,
      };

      if (token) {
        payload.token = token;
      } else if (assessmentId) {
        payload.assessment_id = assessmentId;
      }

      await submitVendorAssessment(payload);

      // Immediately mark in sessionStorage for public page refresh protection
      if (token) {
        sessionStorage.setItem(`vendor_questionnaire_submitted_${token}`, "submitted");
      }

      setShowSuccess(true);
      
      if (onSubmitSuccess) {
        setTimeout(() => {
          onSubmitSuccess();
        }, 2000);
      }
    } catch (error) {
      showSnackbar(error.message || "Failed to submit questionnaire", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const calculateSectionCompletion = (section) => {
    if (!section.questions || section.questions.length === 0) return 0;
    
    const answeredCount = section.questions.filter((q) => {
      const answerId = q.vendor_risk_assessment_answer_id;
      return answers[answerId];
    }).length;
    
    return Math.round((answeredCount / section.questions.length) * 100);
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return "#4caf50";
    if (percentage >= 60) return "#ff9800";
    return "#f44336";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#eeeeee",
        }}
      >
        <CircularProgress size={60} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!questionnaire) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#eeeeee", p: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontFamily: "Lato", color: "#f44336" }}>
            Questionnaire not found or link has expired
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", fontFamily: "Lato" }}>
      {/* Tabs - PolicyDoc style */}
      <Box className="policy-tabs-container">
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="Questionnaire Tabs"
        >
          <Tab className="policy-tab" label="Instructions" />
          <Tab className="policy-tab" label="Questionnaire" />
        </Tabs>
      </Box>

      {/* Instructions Tab */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 3 }}
              >
                This guide provides step-by-step instructions to help users complete the
                vendor security questionnaire within the WhizzC platform.
              </Typography>
              <List>
                {INSTRUCTIONS.map((instruction) => (
                  <ListItem key={instruction.step} sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 1 }}>
                          Step {instruction.step}: {instruction.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography sx={{ fontFamily: "Lato", mb: 1 }}>
                            {instruction.text}
                          </Typography>
                          {instruction.bullets && (
                            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                              {instruction.bullets.map((bullet, idx) => (
                                <li key={idx}>
                                  <Typography sx={{ fontFamily: "Lato" }}>{bullet}</Typography>
                                </li>
                              ))}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 3, textAlign: "right" }}>
                <ReusableButton
                  label="Next"
                  buttonType="padding"
                  onClick={() => setCurrentTab(1)}
                />
              </Box>
          </Paper>
        </Box>
      )}

      {/* Questionnaire Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontFamily: "Lato", fontWeight: "bold" }}>
                  {questionnaire.categories?.[0]?.category_name
                    ? `Category - ${questionnaire.categories[0].category_name}`
                    : "Security Questionnaire"}
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <ReusableButton
                    label="Save as Draft"
                    buttonType="padding"
                    onClick={handleSaveDraft}
                    disabled={submitting}
                  />
                  {isSelfMode ? (
                    <ReusableButton
                      label={sendingForReview ? "Sending..." : "Send for Vendor Review"}
                      buttonType="padding"
                      onClick={handleSendForReview}
                      disabled={sendingForReview || hasIncompleteComments() || !isSubmissionReady()}
                    />
                  ) : (
                    <ReusableButton
                      label={submitting ? "Submitting..." : (assessmentStatus === "APPROVAL_FLAGGED" ? "Re-submit for Approval" : "Send for Approval")}
                      buttonType="padding"
                      onClick={handleSubmit}
                      disabled={submitting || hasIncompleteComments() || !isSubmissionReady()}
                    />
                  )}
                </Box>
              </Box>

              {/* Sections */}
              {(() => {
                let globalSectionIndex = 0;
                return questionnaire.categories?.map((category) =>
                  category.subcategories?.map((subcategory, subcatIdx) => (
                    <React.Fragment key={`subcat-${subcategory.subcategory_id || subcatIdx}`}>
                      {/* Subcategory Header */}
                      {category.subcategories.length > 1 && (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontFamily: "Lato",
                            fontWeight: "bold",
                            color: "#000035",
                            mt: subcatIdx > 0 ? 3 : 1,
                            mb: 1,
                            px: 1,
                            fontSize: "1rem",
                          }}
                        >
                          {subcategory.subcategory_name}
                        </Typography>
                      )}
                      {subcategory.sections?.map((section) => {
                        globalSectionIndex++;
                        const currentSectionNum = globalSectionIndex;
                        const completion = calculateSectionCompletion(section);
                        const sectionId = section.section_id || `section-${subcategory.subcategory_id}-${currentSectionNum}`;

                    return (
                      <Paper
                        key={sectionId}
                        sx={{
                          mb: 2,
                          border: `1px solid #e0e0e0`,
                        }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",
                            bgcolor: "#f5f5f5",
                            "&:hover": { bgcolor: "#eeeeee" },
                          }}
                          onClick={() => handleSectionToggle(sectionId)}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontFamily: "Lato", fontWeight: "bold" }}
                          >
                            Section {currentSectionNum}: {section.section_name || "Unnamed Section"}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {(assessmentStatus === "VENDOR_FLAGGED" || assessmentStatus === "APPROVAL_FLAGGED") ? (
                              <Chip
                                icon={<FlagIcon sx={{ fontSize: 16 }} />}
                                label={`${section.questions?.length || 0} Flagged`}
                                size="small"
                                sx={{
                                  bgcolor: "#fff3e0",
                                  color: "#e65100",
                                  fontFamily: "Lato",
                                  fontWeight: "bold",
                                  border: "1px solid #ffcc80",
                                  "& .MuiChip-icon": { color: "#e65100" },
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: "Lato",
                                  fontWeight: "bold",
                                  color: getCompletionColor(completion),
                                }}
                              >
                                {completion}%
                              </Typography>
                            )}
                            <IconButton>
                              {expandedSections[sectionId] ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </Box>
                        </Box>

                        <Collapse in={expandedSections[sectionId]}>
                          <Box sx={{ bgcolor: SECTION_HEADER_BG, p: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontFamily: "Lato", fontWeight: "bold" }}
                            >
                              {section.section_name || "Questions"}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 3 }}>
                            {section.questions?.map((question, qIndex) => {
                              const answerId = question.vendor_risk_assessment_answer_id;
                              const currentAnswer = answers[answerId];
                              const isSaving = savingAnswers[answerId];
                              const needsComment =
                                currentAnswer === VENDOR_CHOICE_OPTIONS.NO ||
                                currentAnswer === VENDOR_CHOICE_OPTIONS.PARTIAL;
                              const isShaking = shakingComments[answerId];

                              return (
                                <Box
                                  key={answerId}
                                  sx={{
                                    mb: 3,
                                    p: 2,
                                    bgcolor: question.is_flagged ? "#fff3e0" : "white",
                                    borderRadius: 1,
                                    border: question.is_flagged
                                      ? "2px solid #ff9800"
                                      : "1px solid #e0e0e0",
                                  }}
                                >
                                  {/* Vendor Flag Indicator */}
                                  {question.is_flagged && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1.5,
                                        p: 1.5,
                                        bgcolor: "#fff8e1",
                                        borderRadius: 1,
                                        border: "1px solid #ffe082",
                                      }}
                                    >
                                      <FlagIcon sx={{ color: "#f57c00", fontSize: 20 }} />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "Lato",
                                          fontWeight: "bold",
                                          color: "#e65100",
                                        }}
                                      >
                                        Flagged by Vendor:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "Lato",
                                          color: "#bf360c",
                                          fontStyle: "italic",
                                          flex: 1,
                                        }}
                                      >
                                        {question.flag_comment || "No comment provided"}
                                      </Typography>
                                    </Box>
                                  )}
                                  {/* Previously Flagged Indicator */}
                                  {!question.is_flagged && question.was_previously_flagged && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1.5,
                                        p: 1,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Chip
                                        icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                                        label="Previously Flagged"
                                        size="small"
                                        sx={{
                                          fontFamily: "Lato",
                                          bgcolor: "#e3f2fd",
                                          color: "#1565c0",
                                          "& .MuiChip-icon": { color: "#1565c0" },
                                        }}
                                      />
                                    </Box>
                                  )}
                                  {/* Comment History */}
                                  {question.comment_history && question.comment_history.length > 0 && (
                                    <Box
                                      sx={{
                                        mb: 1.5,
                                        p: 1.5,
                                        bgcolor: "#f5f5f5",
                                        borderRadius: 1,
                                        border: "1px solid #e0e0e0",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 1, color: "#333" }}
                                      >
                                        Comment History:
                                      </Typography>
                                      {question.comment_history.map((h, idx) => (
                                        <Box
                                          key={idx}
                                          sx={{
                                            mb: 0.5,
                                            pl: 1,
                                            borderLeft: h.comment_type === "review_comment"
                                              ? "3px solid #f44336"
                                              : "3px solid #1976d2",
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontFamily: "Lato",
                                              color: h.comment_type === "review_comment" ? "#d32f2f" : "#1565c0",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {h.comment_type === "review_comment" ? "Review" : "Answer"} — Round {h.round_number}
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontFamily: "Lato", color: "#555" }}>
                                            {h.comment_text}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontFamily: "Lato", fontWeight: "bold", flex: 1 }}
                                    >
                                      {qIndex + 1}. {question.question_description || "Question"}
                                    </Typography>
                                    {isSaving && <CircularProgress size={16} />}
                                  </Box>

                                  {/* Options (left) + Comment (right) side by side */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 3,
                                      mt: 2,
                                      alignItems: "flex-start",
                                      flexWrap: { xs: "wrap", md: "nowrap" },
                                    }}
                                  >
                                    {/* Left: Answer Options */}
                                    <Box sx={{ flex: "1 1 50%", minWidth: 0 }}>
                                      <RadioGroup
                                        value={currentAnswer || ""}
                                        onChange={(e) =>
                                          handleAnswerChange(answerId, e.target.value, question)
                                        }
                                      >
                                        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                          {Object.entries(CHOICE_LABELS).map(([value, label]) => (
                                            <FormControlLabel
                                              key={value}
                                              value={value}
                                              control={
                                                <Radio
                                                  sx={{
                                                    color: PRIMARY,
                                                    "&.Mui-checked": { color: PRIMARY },
                                                  }}
                                                />
                                              }
                                              label={
                                                <Typography sx={{ fontFamily: "Lato" }}>
                                                  {label}
                                                </Typography>
                                              }
                                            />
                                          ))}
                                        </Box>
                                      </RadioGroup>
                                    </Box>

                                    {/* Right: Comment Box (always visible) */}
                                    <Box
                                      sx={{
                                        flex: "1 1 50%",
                                        minWidth: 0,
                                        animation: isShaking
                                          ? "commentShake 0.4s ease-in-out 0s 2"
                                          : "none",
                                        "@keyframes commentShake": {
                                          "0%, 100%": { transform: "translateX(0)" },
                                          "15%": { transform: "translateX(-6px)" },
                                          "30%": { transform: "translateX(6px)" },
                                          "45%": { transform: "translateX(-4px)" },
                                          "60%": { transform: "translateX(4px)" },
                                          "75%": { transform: "translateX(-2px)" },
                                          "90%": { transform: "translateX(2px)" },
                                        },
                                      }}
                                    >
                                      <TextField
                                        placeholder={
                                          needsComment
                                            ? currentAnswer === VENDOR_CHOICE_OPTIONS.NO
                                              ? "Justification for Non-Compliance (Required)"
                                              : "Comment (Required for Partial)"
                                            : "Add a comment (Optional)"
                                        }
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={comments[answerId] || ""}
                                        onChange={(e) => handleCommentChange(answerId, e.target.value)}
                                        onBlur={() => handleCommentBlur(answerId)}
                                        required={needsComment}
                                        error={
                                          needsComment &&
                                          (!comments[answerId] || comments[answerId].trim() === "")
                                        }
                                        helperText={
                                          needsComment &&
                                          (!comments[answerId] || comments[answerId].trim() === "")
                                            ? "Comment is required for this response"
                                            : ""
                                        }
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            fontFamily: "Lato",
                                            overflowX: "hidden",
                                          },
                                          "& textarea": {
                                            overflowX: "hidden",
                                          },
                                          ...(needsComment &&
                                            (!comments[answerId] || comments[answerId].trim() === "") && {
                                              "& .MuiOutlinedInput-root": {
                                                fontFamily: "Lato",
                                                overflowX: "hidden",
                                                borderColor: "#ff9800",
                                              },
                                            }),
                                        }}
                                      />
                                    </Box>
                                  </Box>

                                  {/* Supporting Evidence */}
                                  {currentAnswer === VENDOR_CHOICE_OPTIONS.YES &&
                                    question.is_artifact_mandatory && (
                                      <Box sx={{ mt: 2, p: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{ fontFamily: "Lato", fontWeight: "bold" }}
                                          >
                                            Supporting Evidence:{" "}
                                            {question.evidence_description || "Upload required documents"}
                                          </Typography>
                                          <Tooltip title="Format: pdf, jpg, png | Size: less than 5MB">
                                            <InfoIcon fontSize="small" sx={{ color: "#666" }} />
                                          </Tooltip>
                                        </Box>
                                        {attachments[answerId] && attachments[answerId].length > 0 ? (
                                          <Box>
                                            {attachments[answerId].map((attachment) => (
                                              <Box
                                                key={attachment.attachment_id}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <AttachFileIcon fontSize="small" />
                                                <Typography
                                                  variant="body2"
                                                  sx={{ fontFamily: "Lato", flex: 1 }}
                                                >
                                                  {attachment.file_name || "Attachment"}
                                                </Typography>
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    handleFileRemove(
                                                      answerId,
                                                      attachment.attachment_id
                                                    )
                                                  }
                                                  sx={{ color: "#f44336" }}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            ))}
                                            <Button
                                              variant="outlined"
                                              component="label"
                                              size="small"
                                              startIcon={<AttachFileIcon />}
                                              sx={{
                                                color: PRIMARY,
                                                borderColor: PRIMARY,
                                                textTransform: "none",
                                                fontFamily: "Lato",
                                                mt: 1,
                                              }}
                                            >
                                              Add More Evidence
                                              <input
                                                type="file"
                                                hidden
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => handleFileAttach(answerId, e)}
                                              />
                                            </Button>
                                          </Box>
                                        ) : (
                                          <Button
                                            variant="contained"
                                            component="label"
                                            size="small"
                                            startIcon={<AttachFileIcon />}
                                            sx={{
                                              bgcolor: PRIMARY,
                                              textTransform: "none",
                                              fontFamily: "Lato",
                                              "&:hover": { bgcolor: "#003d5c" },
                                            }}
                                          >
                                            Attach Evidence
                                            <input
                                              type="file"
                                              hidden
                                              accept=".pdf,.jpg,.jpeg,.png"
                                              onChange={(e) => handleFileAttach(answerId, e)}
                                            />
                                          </Button>
                                        )}
                                      </Box>
                                    )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                    </React.Fragment>
                  ))
                );
              })()}
            </Box>
          )}

      {/* Email Dialog for Send for Review */}
      <Dialog
        open={reviewEmailDialogOpen}
        onClose={() => setReviewEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Lato", fontWeight: "bold" }}>
          Send for Vendor Review
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontFamily: "Lato", mb: 2, color: "#666" }}>
            Enter the vendor's email address to send the questionnaire for review and acknowledgement.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Vendor Email *"
            placeholder="vendor@company.com"
            value={reviewToEmail}
            onChange={(e) => setReviewToEmail(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { fontFamily: "Lato" }, "& .MuiInputLabel-root": { fontFamily: "Lato" } }}
          />
          <TextField
            fullWidth
            label="CC Emails (comma-separated)"
            placeholder="cc1@company.com, cc2@company.com"
            value={reviewCcEmails}
            onChange={(e) => setReviewCcEmails(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { fontFamily: "Lato" }, "& .MuiInputLabel-root": { fontFamily: "Lato" } }}
          />
        </DialogContent>
        <DialogActions>
          <ReusableButton
            label="Cancel"
            buttonType="small-1"
            onClick={() => setReviewEmailDialogOpen(false)}
          />
          <ReusableButton
            label="Send for Review"
            buttonType="padding"
            onClick={handleConfirmSendForReview}
            disabled={!reviewToEmail.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <SuccessModal
        open={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          if (onSubmitSuccess) onSubmitSuccess();
        }}
        title="Success!"
        message={
          isSelfMode
            ? "Questionnaire has been sent to the vendor for review. You will be notified once they respond."
            : "Security Questionnaire successfully submitted. Thank you for your time!"
        }
        icon={<CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50" }} />}
      />

      {/* Scroll to Top FAB */}
      <Zoom in={showScrollTop}>
        <Fab
          size="small"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            bgcolor: "#001b2b",
            color: "#fff",
            "&:hover": { bgcolor: "#00bcd4" },
            zIndex: 1300,
          }}
          aria-label="scroll to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Box>
  );
};

export default VendorAssessmentQuestionnaire;
